import type * as MT from '../../motornet/types';
import type * as AV from '../../autovista/types';
import type * as QT from '../../quotations/types';
import {
	autosuggestToMotornet,
	makesToMotornet,
	modelsToMotornet,
	toAutovistaKind,
	vehicleToMotornet,
	versionsToMotornet,
} from '../../autovista/normalize';
import { isMarket } from '../../utils/markets';
import { BaseHttpRequest } from '../core/BaseHttpRequest';
import { CancelablePromise } from '../core/CancelablePromise';
import { ConfigurableModel, ConfigurableTrim } from '../models/CarConfigurator';
import { MarketsEnum } from '../models/Enums';
import { VehicleQuickoQuotationRequestDTO } from '../models/VehicleQuickoQuotationRequestDTO';
import { buildVehiclesQuery } from '../../vehicleList/query';

const EUROTAX_API_BASE_URL = process.env.REACT_APP_EUROTAX_API_BASE_URL ?? '';

// Il mercato ES espone gli stessi endpoint sotto namespace diversi
const IS_ES = isMarket(MarketsEnum.es);
const MOTORNET_NS = IS_ES ? '/autovista' : '/motornet';
const PLATFORM_NS = IS_ES ? '/platform-es' : '/platform';
const CATALOG_NS = '/quotation-api';

/**
 * Su ES i dati veicolo arrivano da Autovista, che espone una forma di risposta
 * completamente diversa. La traduzione vive in `src/autovista/normalize.ts`:
 * qui si decide solo *quali* parametri inviare e *quando* normalizzare, cosi'
 * le firme pubbliche restano identiche per tutti i 13 consumer.
 */

/** `YYYY-MM-01` — usata da Autovista per scegliere il periodo di listino. */
const referenceDateOf = (year?: number, month?: number): string | null =>
	year == null ? null : `${year}-${String(month ?? 1).padStart(2, '0')}-01`;

/**
 * Campi inviati a `POST /platform-es/vehicle-import` dentro
 * `dettaglio_veicolo`.
 */
const ES_IMPORT_DETAIL_FIELDS = [
	'national_vehicle_code',
	'codice_costruttore',
	'dealer',
	'id_bo',
	'city',
	'codice_colore_esterno',
	'colore_esterno',
	'codice_colore_interno',
	'colore_interno',
	'data_disp',
	'data_immatricolazione',
	'data_telaio',
	'giorno_disp',
	'note',
	'promo',
	'promo_note',
	'settimana_prod',
	'targa',
	'telaio',
	'in_evidenza',
	'limited_visibility',
	'lista_noleggiatori_quotatore',
	'document',
	'optionals',
	'extra_sconto_perc',
	'prezzo_listino',
] as const;

/**
 * Adatta il body di import al mercato ES. Due differenze rispetto a IT:
 *
 *  1. `codice_eurotax` si chiama `national_vehicle_code` (unica differenza fra
 *     i due schemi: per il resto i 25 campi coincidono).
 *  2. si filtra sulla allowlist qui sopra — non per un vincolo del BE, che
 *     accetta tutto, ma per non spedire i campi di sola UI che Formik porta
 *     con se'.
 *
 * Il form espone il giorno di disponibilita' come `giorno_disp_horizon`
 * (`vehicleForm.emptyInstanceValues`) e nessun chiamante lo rimappa, per cui lo
 * normalizziamo qui — altrimenti verrebbe scartato dalla allowlist e il dato
 * andrebbe perso.
 */
export const toEsImportRequest = (body: MT.PlatformVehicleImportRequest) => {
	const source = body.dettaglio_veicolo as unknown as Record<string, unknown>;

	const normalized: Record<string, unknown> = {
		...source,
		national_vehicle_code: source.codice_eurotax,
		giorno_disp: source.giorno_disp ?? source.giorno_disp_horizon,
	};

	const dettaglio_veicolo: Record<string, unknown> = {};
	for (const field of ES_IMPORT_DETAIL_FIELDS) {
		if (normalized[field] !== undefined) {
			dettaglio_veicolo[field] = normalized[field];
		}
	}

	return { ...body, dettaglio_veicolo };
};

async function throwMotornetResponseError(res: Response): Promise<never> {
	const raw = await res.text();
	let body: unknown = raw;
	try {
		body = raw ? JSON.parse(raw) : null;
	} catch {}

	const message =
		(body && typeof body === 'object' && 'message' in body
			? String((body as { message?: unknown }).message)
			: body && typeof body === 'object' && 'error' in body
			? String((body as { error?: unknown }).error)
			: typeof body === 'string' && body
			? body
			: res.statusText) || `HTTP ${res.status}`;

	const error = new Error(message) as Error & {
		status?: number;
		statusText?: string;
		body?: unknown;
	};
	error.status = res.status;
	error.statusText = res.statusText;
	error.body = body;
	throw error;
}

const catalogFilterQuery = (filters: MT.CatalogRowFilters): URLSearchParams => {
	const query = new URLSearchParams();
	if (filters.vehicleKind) query.set('vehicleKind', filters.vehicleKind);
	if (filters.providers?.length) query.set('providers', filters.providers.join(','));
	if (filters.minMatches != null) query.set('minMatches', String(filters.minMatches));
	if (filters.state) query.set('state', filters.state);
	if (filters.hasStock !== undefined) query.set('hasStock', String(filters.hasStock));
	if (filters.matrixAutoRenew !== undefined) query.set('matrixAutoRenew', String(filters.matrixAutoRenew));
	if (filters.search) query.set('search', filters.search);
	return query;
};

export class MotornetControllerService {
	constructor(public readonly httpRequest: BaseHttpRequest) {}

	private async currentToken(): Promise<string | undefined> {
		const { TOKEN } = this.httpRequest.config;
		const token = typeof TOKEN === 'function' ? await TOKEN({} as any) : TOKEN;
		return token || undefined;
	}

	private async authHeaders(): Promise<Record<string, string>> {
		const token = await this.currentToken();

		const headers: Record<string, string> = {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		return headers;
	}

	public async getMakes(): Promise<MT.MotornetMakeWithKind[]> {
		const headers = await this.authHeaders();

		const res = await fetch(`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/makes`, {
			headers,
		});

		if (!res.ok) return throwMotornetResponseError(res);

		const data = await res.json();
		return IS_ES ? makesToMotornet(data as AV.AutovistaMake[]) : data;
	}

	public async getModels(
		codiceMarca: string,
		options: { vehicleKind?: MT.MotornetVehicleKind; year?: number } = {}
	): Promise<MT.MotornetModelSummary[]> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({ codice_marca: codiceMarca });

		if (IS_ES) {
			// Autovista accetta solo auto|4wd|vcom e non filtra per anno.
			const kind = toAutovistaKind(options.vehicleKind);
			if (kind) query.set('vehicleKind', kind);
		} else {
			if (options.vehicleKind) query.set('vehicleKind', options.vehicleKind);
			if (options.year != null) query.set('year', String(options.year));
		}

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/models?${query}`,
			{ headers }
		);

		if (!res.ok) return throwMotornetResponseError(res);

		const data = await res.json();
		return IS_ES ? modelsToMotornet(data as AV.AutovistaModel[]) : data;
	}

	public async getVersions(params: {
		codiceModello: string;
		codiceMarca: string;
		vehicleKind?: MT.MotornetVehicleKind;
		year?: number;
		codiceAlimentazione?: string;
		tipologia?: string;
	}): Promise<MT.MotornetVersionSummary[]> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			codice_modello: params.codiceModello,
			codice_marca: params.codiceMarca,
		});

		if (IS_ES) {
			// Autovista non supporta anno / tipologia / alimentazione come filtri.
			const kind = toAutovistaKind(params.vehicleKind);
			if (kind) query.set('vehicleKind', kind);
		} else {
			if (params.vehicleKind) query.set('vehicleKind', params.vehicleKind);
			if (params.year != null) query.set('year', String(params.year));
			if (params.tipologia) query.set('tipologia', params.tipologia);

			if (params.codiceAlimentazione) {
				query.set('codice_alimentazione', params.codiceAlimentazione);
			}
		}

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/versions?${query}`,
			{ headers }
		);

		if (!res.ok) return throwMotornetResponseError(res);

		const data = await res.json();
		return IS_ES ? versionsToMotornet(data as AV.AutovistaVehicleType[]) : data;
	}

	public async getVersionsList(
		body: MT.MotornetVersionsListRequest,
		forceRefresh = false
	): Promise<MT.MotornetVersionsListResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			forceRefresh: String(forceRefresh),
		});

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/versions/list?${query}`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getMotornetVehicle(
		eurotaxCode: string,
		vehicleKind?: MT.MotornetVehicleKind,
		options: { year?: number; month?: number; forceRefresh?: boolean } = {}
	): Promise<MT.MotornetVehicleResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			forceRefresh: String(options.forceRefresh ?? false),
		});

		if (IS_ES) {
			// Autovista identifica il veicolo con il NationalVehicleCode e
			// accetta la data di immatricolazione, non anno/mese di listino.
			query.set('nationalVehicleCode', eurotaxCode);
			if (options.year != null) {
				query.set('registrationYear', String(options.year));
			}
			if (options.month != null) {
				query.set('registrationMonth', String(options.month));
			}
		} else {
			query.set('eurotaxCode', eurotaxCode);
			if (vehicleKind) query.set('vehicleKind', vehicleKind);
			if (options.year != null) query.set('year', String(options.year));
			if (options.month != null) query.set('month', String(options.month));
		}

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/vehicle?${query}`,
			{
				headers,
			}
		);

		if (!res.ok) return throwMotornetResponseError(res);

		const data = await res.json();
		return IS_ES
			? vehicleToMotornet(data as AV.AutovistaVehiclePayload, {
					referenceDate: referenceDateOf(options.year, options.month),
					year: options.year,
					month: options.month,
			  })
			: data;
	}

	/**
	 * Su ES `makeCode` e' obbligatorio: Autovista espone
	 * GetListTypeByManufacturerCode solo per CH/ES e solo insieme alla marca.
	 * Su IT viene ignorato.
	 */
	public async getVehicleByManufactureCode(
		manufactureCode: string,
		forceRefresh = false,
		makeCode?: string
	): Promise<MT.MotornetVehicleByManufactureCodeResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			manufactureCode,
			forceRefresh: String(forceRefresh),
		});
		if (IS_ES && makeCode) query.set('makeCode', makeCode);

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/vehicle-by-manufacture-code?${query}`,
			{ headers }
		);

		if (!res.ok) return throwMotornetResponseError(res);

		const data = await res.json();
		return IS_ES
			? vehicleToMotornet(data as AV.AutovistaVehiclePayload)
			: data;
	}

	/** Vedi {@link getVehicleByManufactureCode} per il vincolo su `makeCode`. */
	public async getVehicleAutosuggestByManufactureCode(
		manufactureCode: string,
		forceRefresh = false,
		makeCode?: string
	): Promise<MT.MotornetAutosuggestResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			manufactureCode,
			forceRefresh: String(forceRefresh),
		});
		if (IS_ES && makeCode) query.set('makeCode', makeCode);

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/vehicle-autosuggest-by-manufacture-code?${query}`,
			{ headers }
		);

		if (!res.ok) return throwMotornetResponseError(res);

		const data = await res.json();
		return IS_ES
			? autosuggestToMotornet(
					data as AV.AutovistaVehicleType[],
					manufactureCode
			  )
			: data;
	}

	/**
	 * Su ES non esiste `/autovista/vehicle-validate`.
	 */
	public async validateVehicleOptions(
		body: MT.MotornetVehicleValidateRequest
	): Promise<MT.MotornetVehicleOptionValidationResult> {
		if (IS_ES) {
			const selected = body.selectedOptionIds ?? [];
			return {
				valid: true,
				eurotaxCode: body.eurotaxCode,
				vehicleKind: body.vehicleKind,
				requestedOptionIds: selected,
				selectedOptionIds: selected,
				disabledOptionIds: [],
				excludedOptionIds: [],
				includedOptionIds: [],
				constrainedOptionIds: [],
				selectedOptions: [],
				disabledOptions: [],
				errors: [],
			};
		}

		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/vehicle-validate`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async validateImportRows(
		body: MT.MotornetVehicleImportValidateRequest,
		forceRefresh = false
	): Promise<MT.MotornetVehicleImportValidationResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			forceRefresh: String(forceRefresh),
		});

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${MOTORNET_NS}/vehicle-import-validate?${query}`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async importVehicle(
		body: MT.PlatformVehicleImportRequest,
		simulate = false
	): Promise<MT.PlatformVehicleImportResponse> {
		const headers = await this.authHeaders();

		// `vehicle-import-simulation` esiste solo su ES: valida il body e risponde
		// con la stessa PlatformVehicleImportResponse senza persistere nulla.
		// Su IT il flag e' ignorato.
		const path =
			IS_ES && simulate ? '/vehicle-import-simulation' : '/vehicle-import';

		const res = await fetch(`${EUROTAX_API_BASE_URL}${PLATFORM_NS}${path}`, {
			method: 'POST',
			body: JSON.stringify(IS_ES ? toEsImportRequest(body) : body),
			headers,
		});

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async importVehicleExcel(
		body: MT.PlatformVehicleImportExcelRequest
	): Promise<MT.PlatformVehicleImportResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/vehicle-import-excel`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getQuotable(
		fallbackOnError = true
	): Promise<MT.PlatformQuotableResponse> {
		try {
			const headers = await this.authHeaders();

			const res = await fetch(
				`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/quotable`,
				{
					headers,
				}
			);

			if (res.ok) {
				return res.json();
			}

			return throwMotornetResponseError(res);
		} catch (err) {
			if (fallbackOnError) {
				return {
					total: 0,
					quotableCount: 0,
					notQuotableCount: 0,
					quotableIds: [],
					notQuotableIds: [],
					results: [],
				};
			}
			throw err;
		}
	}

	public async getPlatformVehicles(
		params: MT.PlatformVehiclesRequest = {}
	): Promise<MT.PlatformVehiclesResponse> {
		const headers = await this.authHeaders();
		const queryString = buildVehiclesQuery(params).toString();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/vehicles${
				queryString ? `?${queryString}` : ''
			}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getQuotationsParameters(
		vehicleCode: string
	): Promise<MT.PlatformQuotationsParameters> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({ vehicleCode });

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/quotations-parameters?${query}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async createQuotation(
		body: VehicleQuickoQuotationRequestDTO
	): Promise<MT.PlatformQuotationResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/quotation`, {
			method: 'POST',
			body: JSON.stringify(body),
			headers,
		});

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getEurotaxBackfillVehicles(
		params: MT.EurotaxBackfillVehiclesRequest = {}
	): Promise<MT.EurotaxBackfillVehiclesResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams();
		if (params.filter) {
			query.set('filter', params.filter);
		}
		if (params.manufactureCode) {
			query.set('manufactureCode', params.manufactureCode);
		}
		if (params.page != null) {
			query.set('page', String(params.page));
		}
		if (params.pageSize != null) {
			query.set('pageSize', String(params.pageSize));
		}
		const queryString = query.toString();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/eurotax-backfill/vehicles${
				queryString ? `?${queryString}` : ''
			}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async resolveEurotaxBackfill(
		body: MT.EurotaxBackfillResolveRequest
	): Promise<MT.EurotaxBackfillResolveResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/eurotax-backfill`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async setEurotaxBackfillPriceOverride(
		mongoId: string,
		body: MT.EurotaxBackfillPriceOverrideRequest
	): Promise<MT.EurotaxBackfillPriceOverrideResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/eurotax-backfill/price-override/${encodeURIComponent(
				mongoId
			)}`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async retryEurotaxBackfill(
		mongoId: string,
		body: any = {}
	): Promise<MT.EurotaxBackfillRetryResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/eurotax-backfill/retry/${encodeURIComponent(
				mongoId
			)}`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getPlatformConfigurableMakes(): Promise<
		MT.PlatformConfigurableMake[]
	> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurable/makes`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getPlatformConfigurableModels(
		makeCode: string
	): Promise<MT.PlatformConfigurableModel[]> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({ makeCode });

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurable/models?${query}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getPlatformConfigurableList(
		params: MT.PlatformConfigurableListRequest = {}
	): Promise<MT.PlatformConfigurableListResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams();
		if (params.make) query.set('make', params.make);
		if (params.model) query.set('model', params.model);
		if (params.excluded != null) query.set('excluded', String(params.excluded));
		if (params.page != null) query.set('page', String(params.page));
		if (params.pageSize != null) query.set('pageSize', String(params.pageSize));
		const queryString = query.toString();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurable/list${
				queryString ? `?${queryString}` : ''
			}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async excludeConfigurable(
		vehicleCode: string
	): Promise<MT.PlatformConfigurableExcludeResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurable/exclude`,
			{
				method: 'POST',
				body: JSON.stringify({ vehicle_code: vehicleCode }),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async unexcludeConfigurable(
		vehicleCode: string
	): Promise<MT.PlatformConfigurableExcludeResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurable/unexclude`,
			{
				method: 'POST',
				body: JSON.stringify({ vehicle_code: vehicleCode }),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	/**
	 * Veicoli di stock HMP compatibili con l'allestimento selezionato.
	 */
	public async getConfiguratorStock(
		params: MT.ConfigurableStockRequest
	): Promise<MT.ConfigurableStockResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams({
			makeCode: params.makeCode,
			modelCode: params.modelCode,
		});

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurable-stock?${query}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getCarConfigurations(
		params: MT.PlatformConfigurationListRequest = {}
	): Promise<MT.PlatformConfigurationListResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams();
		if (params.q) query.set('q', params.q);
		if (params.make) query.set('make', params.make);
		if (params.model) query.set('model', params.model);
		if (params.page != null) query.set('page', String(params.page));
		if (params.pageSize != null) query.set('pageSize', String(params.pageSize));
		if (params.sortBy) query.set('sortBy', params.sortBy);
		if (params.sortDirection) query.set('sortDirection', params.sortDirection);
		const queryString = query.toString();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurations${
				queryString ? `?${queryString}` : ''
			}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getCarConfiguration(
		id: string
	): Promise<MT.PlatformConfiguration> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurations/${encodeURIComponent(
				id
			)}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async saveCarConfiguration(
		body: MT.SaveConfigurationBody
	): Promise<MT.PlatformConfiguration> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurations`,
			{
				method: 'POST',
				body: JSON.stringify(body),
				headers,
			}
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async deleteCarConfiguration(
		id: string
	): Promise<MT.DeleteConfigurationResponse> {
		const headers = await this.authHeaders();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/configurations/${encodeURIComponent(
				id
			)}/delete`,
			{ method: 'POST', body: JSON.stringify({}), headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	private async fetchCatalog(path: string, init: RequestInit = {}): Promise<Response> {
		const headers = await this.authHeaders();
		const res = await fetch(`${EUROTAX_API_BASE_URL}${CATALOG_NS}${path}`, { ...init, headers });
		return res.ok ? res : throwMotornetResponseError(res);
	}

	private async getCatalog<T>(path: string, query?: URLSearchParams): Promise<T> {
		const queryString = query?.toString();
		const res = await this.fetchCatalog(`${path}${queryString ? `?${queryString}` : ''}`);
		return res.json();
	}

	private async postCatalog<T>(path: string, body: unknown): Promise<T> {
		const res = await this.fetchCatalog(path, { method: 'POST', body: JSON.stringify(body) });
		return res.json();
	}

	// 202: modifica salvata ma codici Motornet non ancora aggiornati, va ripetuta la stessa chiamata
	private async postProviderChange(path: string, body: unknown): Promise<MT.CatalogProviderChangeResponse> {
		const res = await this.fetchCatalog(path, { method: 'POST', body: JSON.stringify(body) });
		if (res.status === 202) return { version: null, movedFrom: [], vehiclesPending: true };
		return { ...(await res.json()), vehiclesPending: false };
	}

	public getCatalogRows(params: MT.CatalogRowsRequest = {}): Promise<MT.CatalogRowsResponse> {
		const query = catalogFilterQuery(params);
		if (params.make) query.set('make', params.make);
		if (params.model) query.set('model', params.model);
		if (params.page != null) query.set('page', String(params.page));
		if (params.pageSize != null) query.set('pageSize', String(params.pageSize));
		return this.getCatalog('/catalog-versions', query);
	}

	public getCatalogMakes(filters: MT.CatalogRowFilters = {}): Promise<MT.CatalogFacetResponse> {
		return this.getCatalog('/catalog-versions/makes', catalogFilterQuery(filters));
	}

	public getCatalogModels(filters: MT.CatalogRowFilters, make: string): Promise<MT.CatalogFacetResponse> {
		const query = catalogFilterQuery(filters);
		query.set('make', make);
		return this.getCatalog('/catalog-versions/models', query);
	}

	public getCatalogCandidates(params: MT.CatalogCandidatesRequest): Promise<MT.CatalogCandidatesResponse> {
		const query = new URLSearchParams({ provider: params.provider });
		if (params.make) query.set('make', params.make);
		if (params.model) query.set('model', params.model);
		if (params.version) query.set('version', params.version);
		if (params.q) query.set('q', params.q);
		if (params.page != null) query.set('page', String(params.page));
		return this.getCatalog('/catalog-versions/candidates', query);
	}

	public linkCatalogProvider(body: MT.CatalogLinkBody): Promise<MT.CatalogProviderChangeResponse> {
		return this.postProviderChange('/catalog-versions/providers/link', body);
	}

	public removeCatalogProvider(body: MT.CatalogRemoveBody): Promise<MT.CatalogProviderChangeResponse> {
		return this.postProviderChange('/catalog-versions/providers/remove', body);
	}

	public markCatalogProviderUnavailable(body: MT.CatalogUnavailableBody): Promise<{ version: MT.CatalogRow }> {
		return this.postCatalog('/catalog-versions/providers/unavailable', body);
	}

	public markCatalogProviderAvailable(body: MT.CatalogRemoveBody): Promise<{ version: MT.CatalogRow }> {
		return this.postCatalog('/catalog-versions/providers/available', body);
	}

	public setCatalogMatrixAutoRenew(body: MT.CatalogMatrixAutoRenewBody): Promise<{ version: MT.CatalogRow }> {
		return this.postCatalog('/catalog-versions/matrix-auto-renew', body);
	}

	public setCatalogMotornet(body: MT.CatalogMotornetBody): Promise<{ version: MT.CatalogRow }> {
		return this.postCatalog('/catalog-versions/motornet', body);
	}

	public findCatalogMotornet(body: MT.CatalogMotornetAutoBody): Promise<MT.CatalogMotornetAutoResponse> {
		return this.postCatalog('/catalog-versions/motornet/auto', body);
	}

	public async saveQuotation(
		body: QT.SaveQuotationBody
	): Promise<QT.Quotation> {
		const headers = await this.authHeaders();

		const res = await fetch(`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/quotes`, {
			method: 'POST',
			body: JSON.stringify(body),
			headers,
		});

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public async getQuotations(
		params: QT.QuotationListRequest = {}
	): Promise<QT.QuotationListResponse> {
		const headers = await this.authHeaders();
		const query = new URLSearchParams();
		if (params.q) query.set('q', params.q);
		if (params.source) query.set('source', params.source);
		if (params.code) query.set('code', params.code);
		if (params.make) query.set('make', params.make);
		if (params.model) query.set('model', params.model);
		if (params.dealer) query.set('dealer', params.dealer);
		if (params.createdAtFrom) query.set('createdAtFrom', params.createdAtFrom);
		if (params.createdAtTo) query.set('createdAtTo', params.createdAtTo);
		if (params.page != null) query.set('page', String(params.page));
		if (params.pageSize != null) query.set('pageSize', String(params.pageSize));
		if (params.sortBy) query.set('sortBy', params.sortBy);
		if (params.sortDirection) query.set('sortDirection', params.sortDirection);
		const queryString = query.toString();

		const res = await fetch(
			`${EUROTAX_API_BASE_URL}${PLATFORM_NS}/quotes${
				queryString ? `?${queryString}` : ''
			}`,
			{ headers }
		);

		if (res.ok) {
			return res.json();
		}

		return throwMotornetResponseError(res);
	}

	public getConfigurableMakes(): CancelablePromise<Array<string>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables/makes',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getConfigurableModels(
		make: string
	): CancelablePromise<Array<ConfigurableModel>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables/models',
			query: { make },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getConfigurableTrims(
		make: string,
		model: string
	): CancelablePromise<Array<ConfigurableTrim>> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/vehicles/configurables/versions',
			query: { make, model },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}
}
